import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [impersonator, setImpersonator] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get('/api/auth/me');
      setUser(data.user);
      setAccount(data.account || null);
      setMustChangePassword(Boolean(data.mustChangePassword));
      setImpersonator(data.impersonator || null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setAccount(null);
        setMustChangePassword(false);
        setImpersonator(null);
      } else {
        console.error('auth refresh failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await api.post('/api/auth/login', { email, password });
    setUser(data.user);
    setAccount(data.account || null);
    setMustChangePassword(Boolean(data.mustChangePassword));
    setImpersonator(null);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (err) {
      console.error('logout failed', err);
    } finally {
      setUser(null);
      setAccount(null);
      setMustChangePassword(false);
      setImpersonator(null);
    }
  }, []);

  // End an active impersonation session: server clears the impersonation
  // session row + restores the admin's primary cookie, then we refresh
  // /api/auth/me so the UI re-renders as the admin.
  const endImpersonation = useCallback(async () => {
    const res = await api.post('/api/impersonation/end');
    await refresh();
    return res;
  }, [refresh]);

  // Called from the change-password UI once the endpoint returns 200. Clears
  // the gate so subsequent navigation flows normally without another refresh.
  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
  }, []);

  const value = {
    user,
    account,
    mustChangePassword,
    impersonator,
    loading,
    login,
    logout,
    refresh,
    clearMustChangePassword,
    endImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
